import type { BrandColors, BrandDto, KnowledgeDocDto, MediaDto } from '@studio/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ImageSlots } from '@/components/ImageSlots';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog, NativeSelect, SwitchRow } from '@/components/ui/controls';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/form';
import { PageHeader, Skeleton } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useBrand, useKnowledge } from '@/lib/queries';
import { ago } from '@/lib/utils';

const COLOR_LABELS: Record<keyof BrandColors, string> = {
  primary: 'Brand green',
  accent: 'ORDER NOW text',
  cardBackground: 'Card background',
  cardText: 'Card text',
  cardOutline: 'Card outline',
};

function BrandCard({ brand }: { brand: BrandDto }) {
  const qc = useQueryClient();
  const [name, setName] = useState(brand.name);
  const [logo, setLogo] = useState<MediaDto[]>(brand.logo ? [brand.logo] : []);
  const [buttonText, setButtonText] = useState(brand.buttonText);
  const [linkLabel, setLinkLabel] = useState(brand.linkLabel);
  const [colors, setColors] = useState(brand.colors);
  const save = useMutation({
    mutationFn: () =>
      api<BrandDto>('/api/brand', {
        method: 'PATCH',
        body: { name, logoId: logo[0]?.id ?? null, buttonText, linkLabel, colors },
      }),
    onSuccess: (b) => {
      qc.setQueryData(['brand'], b);
      toast.success('Brand saved');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Brand</CardTitle>
        <CardDescription>
          The logo is shown top-right in every finished video; the call to action defaults go into new projects.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <Field label="Company name">
          {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <div>
          <div className="mb-1.5 text-sm font-medium">Logo (PNG with transparent background)</div>
          <ImageSlots images={logo} max={1} onChange={setLogo} size="sm" />
        </div>
        <Field label="Button text">
          {(p) => <Input {...p} value={buttonText} onChange={(e) => setButtonText(e.target.value)} />}
        </Field>
        <Field label="Label above the button">
          {(p) => <Input {...p} value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} />}
        </Field>
        <div className="grid grid-cols-2 gap-3 sm:col-span-2 sm:grid-cols-5">
          {(Object.keys(COLOR_LABELS) as (keyof BrandColors)[]).map((k) => (
            <label key={k} className="grid gap-1 text-xs">
              {COLOR_LABELS[k]}
              <input
                type="color"
                value={colors[k]}
                onChange={(e) => setColors({ ...colors, [k]: e.target.value.toUpperCase() })}
                className="h-9 w-full rounded-md border"
              />
            </label>
          ))}
        </div>
        <div className="sm:col-span-2">
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save brand
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DocDialog({
  doc,
  open,
  onOpenChange,
}: {
  doc: KnowledgeDocDto | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<KnowledgeDocDto['kind']>('other');
  const [body, setBody] = useState('');
  const [useInAi, setUseInAi] = useState(true);
  useEffect(() => {
    if (!open) return;
    setTitle(doc?.title ?? '');
    setKind(doc?.kind ?? 'other');
    setBody(doc?.body ?? '');
    setUseInAi(doc?.useInAi ?? true);
  }, [open, doc]);
  const save = useMutation({
    mutationFn: () => {
      const payload = { title, kind, body, useInAi };
      return doc
        ? api(`/api/knowledge/${doc.id}`, { method: 'PATCH', body: payload })
        : api('/api/knowledge', { method: 'POST', body: payload });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['knowledge'] });
      toast.success('Saved');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{doc ? 'Edit document' : 'New document'}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
          <Field label="Title">
            {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}
          </Field>
          <Field label="Kind">
            {(p) => (
              <NativeSelect {...p} value={kind} onChange={(e) => setKind(e.target.value as KnowledgeDocDto['kind'])}>
                <option value="qa">Customer Q&amp;A</option>
                <option value="claims">Claim rules</option>
                <option value="other">Other</option>
              </NativeSelect>
            )}
          </Field>
        </div>
        <Field label="Text">
          {(p) => (
            <Textarea
              {...p}
              rows={16}
              className="font-mono text-xs"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
        </Field>
        <SwitchRow label="Give to the storyboard AI" checked={useInAi} onCheckedChange={setUseInAi} />
        <DialogFooter>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!title.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BrandPage() {
  const qc = useQueryClient();
  const brand = useBrand();
  const docs = useKnowledge();
  const [editing, setEditing] = useState<KnowledgeDocDto | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<KnowledgeDocDto | null>(null);
  const del = useMutation({
    mutationFn: (d: KnowledgeDocDto) => api(`/api/knowledge/${d.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['knowledge'] });
    },
  });
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title="Brand & knowledge"
        description="What the studio knows about Herbal Cure: logo, call to action, company Q&A and the claim rules every ad must follow."
      />
      {brand.data ? <BrandCard brand={brand.data} /> : <Skeleton className="h-64" />}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>Knowledge base</CardTitle>
            <CardDescription>Text the storyboard AI reads before it writes a shot list.</CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus /> Add document
          </Button>
        </CardHeader>
        <CardContent className="divide-y">
          {docs.data?.map((d) => (
            <div key={d.id} className="flex items-center gap-3 py-2.5">
              {d.kind === 'claims' ? (
                <ShieldCheck className="size-4 text-primary" />
              ) : (
                <BookOpen className="size-4 text-muted-foreground" />
              )}
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => {
                  setEditing(d);
                  setOpen(true);
                }}
              >
                <div className="text-sm font-medium">{d.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {d.body.length.toLocaleString('en-IN')} characters · updated {ago(d.updatedAt)}
                </div>
              </button>
              {d.kind === 'claims' && <Badge>claim rules</Badge>}
              {!d.useInAi && <Badge variant="secondary">not given to AI</Badge>}
              <Button variant="ghost" size="icon" onClick={() => setDeleting(d)} aria-label="Delete document">
                <Trash2 />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
      <DocDialog doc={editing} open={open} onOpenChange={setOpen} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={`Delete "${deleting?.title}"?`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => deleting && del.mutate(deleting)}
      />
    </div>
  );
}
